<template>
  <div class="composition-list-header">
    <div class="level1">
      <p class="design-text">Designs</p>
      <MarketplaceLogo v-if="isMp" class="logo-small" />
      <SpreadshopLogo v-else class="logo-small" />
      <button class="btn btn-primary icon-btn" @click="create">
        <Icon icon="plus" />
        {{ $t('PUBLISHING.LIST.CREATE_NEW') }}
      </button>
    </div>
    <div class="level2">
      <DmsModule v-if="isMp" condensed />
      <VolumeCommissionModule v-else condensed />
      <DesignCheckBanner />
    </div>
    <div class="level3">
      <div>
        <div class="header-input-label">{{ $t('GENERAL.STATUS') }}</div>
        <PublishingPosStateDropdown
          @pos-state-changed="search"
        ></PublishingPosStateDropdown>
      </div>
      <div>
        <div class="header-input-label">{{ $t('GENERAL.SEARCH') }}</div>
        <div class="search-field">
          <input
            v-model="searchTerm"
            :class="{ filled: searchTerm }"
            :placeholder="`${$t('PUBLISHING.DETAIL.META_DATA.NAME.LABEL')}, Id, ${$t('PUBLISHING.DETAIL.META_DATA.TAGS.LABEL')}`"
            type="text"
            @keyup.enter="updateQuery"
            @blur="updateQuery"
          />
          <button
            class="icon-only-btn reset-link"
            @mousedown="preventBlur"
            @click="clearSearchTerm"
          >
            <Icon icon="close" />
          </button>
          <button class="icon-only-btn search-link">
            <Icon icon="lens" />
          </button>
        </div>
      </div>
      <button
        v-if="searched"
        class="link link-main icon-link clear-all-btn"
        @mousedown="preventBlur"
        @click="clearAllFilters"
      >
        <Icon icon="close" />
        <span class="clear-all-text">{{ $t('FILTERS.RESET') }}</span>
      </button>
      <div v-if="searched" class="search-result-pagination-text">
        {{ searchResultPaginationText }}
      </div>
    </div>
  </div>
</template>

<script setup>
import MarketplaceLogo from 'commons/logos/MarketplaceLogo.vue';
import analytics from 'commons/tracking/analytics';
import { computed, ref, watch } from 'vue';
import PublishingPosStateDropdown from './PublishingPosStateDropdown.vue';
import SpreadshopLogo from 'commons/logos/SpreadshopLogo.vue';
import DmsModule from '../../dashboard/DmsModule.vue';
import VolumeCommissionModule from 'src/app/partnerarea/dashboard/VolumeCommissionModule.vue';
import { PUBLISHING_SCOPE } from 'commons/publishing/publishingScope';
import { usePublishingStore } from 'commons/stores/publishing';
import { useCompositionsStore } from 'commons/stores/compositions';
import DesignCheckBanner from 'src/app/components/publishing/DesignCheckBanner.vue';

const emits = defineEmits(['change', 'create']);
const publishingStore = usePublishingStore();
const compositionsStore = useCompositionsStore();

const searched = computed(() => compositionsStore.searched);
const totalItems = computed(() => compositionsStore.totalCompositionCount);
const query = computed(() => compositionsStore.filter.query);
const currentPage = computed(() => compositionsStore.filter.page);
const pageSize = computed(() => compositionsStore.filter.pageSize);
const isMp = computed(
  () => publishingStore.currentScope?.key === PUBLISHING_SCOPE.MP.key,
);

const searchResultPaginationText = computed(() => {
  if (totalItems.value <= pageSize.value) {
    return `Showing 1-${totalItems.value} of ${totalItems.value}`;
  }

  const startNumber = currentPage.value * pageSize.value + 1;
  const lastPage = Math.ceil(totalItems.value / pageSize.value) - 1;
  const lastItemCount = totalItems.value % pageSize.value || pageSize.value;
  const endNumber =
    currentPage.value < lastPage
      ? startNumber + pageSize.value - 1
      : startNumber + lastItemCount - 1;

  const endNumberText = startNumber === endNumber ? '' : `-${endNumber}`;
  return `Showing ${startNumber}${endNumberText} of ${totalItems.value}`;
});

const searchTerm = ref(query.value);
watch(query, (newQuery) => {
  if (newQuery !== searchTerm.value) {
    searchTerm.value = newQuery;
  }
});

const create = () => emits('create');
const updateSearchParams = (params) => {
  compositionsStore.setFilter({ ...params, page: 0 });
};

const search = () => {
  emits('change');
};

const updateQuery = () => {
  const newQuery = searchTerm.value ? searchTerm.value : null;
  if (newQuery !== query.value) {
    updateSearchParams({ query: newQuery });
    analytics.events.searchCompositions(newQuery);
    search();
  }
};

const clearSearchTerm = () => {
  searchTerm.value = null;
  updateQuery();
};

const clearAllFilters = () => {
  compositionsStore.resetFilter();
  search();
};

const preventBlur = (e) => {
  e.preventDefault();
};
</script>

<style scoped lang="scss">
@import 'src/scss/constants';

.composition-list-header {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  .level1 {
    display: flex;
    align-items: center;
    margin: 24px 0 0 0;
    gap: 24px;
    width: 100%;

    .design-text {
      color: #191919;
      font-size: 32px;
      font-style: normal;
      font-weight: 700;
      line-height: 40px; /* 125% */
      letter-spacing: -0.32px;
      margin: 0;
    }

    & > button {
      margin-left: auto;
      align-self: stretch;
      padding: 12px 16px;
      border-radius: $border-radius-medium;

      .icon {
        width: 16px;
        height: 16px;
        fill: white;
        stroke: white;
      }
    }
  }

  .level2 {
    margin: 24px 0 0 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;

    & > div {
      width: 100%;
    }

    //.module {
    //  display: flex;
    //  align-items: center;
    //}
  }

  .level3 {
    display: flex;
    align-items: center;
    margin: 24px 0 24px 0;
    gap: 24px;
    width: 100%;

    .search-field {
      border-radius: $border-radius-medium;
      overflow: hidden;

      & > input {
        border-radius: $border-radius-medium;
      }
    }
  }

  .clear-all-btn {
    margin-top: 1.5rem;
  }

  .search-result-pagination-text {
    margin-left: auto;
    margin-top: auto;
    padding: 0 5px 5px 0;
  }

  .header-input-label {
    margin-bottom: 3px;
  }

  .logo-small {
    width: 125px;
    height: 20px;
    flex-shrink: 0;
  }
}
</style>
